import { NavLink, Outlet } from "react-router-dom";
import { Logo, Button, Card } from "@/design/components";
import { HOWTO_URL } from "@/config";
import { useAuth } from "./auth";
import {
  IconDashboard,
  IconChannels,
  IconIntegrations,
  IconMail,
  IconTeam,
  IconBilling,
  IconActivity,
  IconSettings,
  IconLogout,
} from "@/design/icons";
import styles from "./AppShell.module.css";

const NAV = [
  { to: "/app", label: "Dashboard", Icon: IconDashboard, end: true },
  { to: "/app/channels", label: "Channels", Icon: IconChannels, end: false },
  { to: "/app/integrations", label: "Integrations", Icon: IconIntegrations, end: false },
  { to: "/app/mail", label: "Mail", Icon: IconMail, end: false },
  { to: "/app/team", label: "Team", Icon: IconTeam, end: false },
  { to: "/app/billing", label: "Billing", Icon: IconBilling, end: false },
  { to: "/app/audit", label: "Activity", Icon: IconActivity, end: false },
  { to: "/app/settings", label: "Settings", Icon: IconSettings, end: false },
];

/**
 * Persistent authenticated layout (a React Router layout route — the vault's
 * nested-layout / shared-wrapper pattern). Sidebar nav + topbar chrome wrap the
 * routed <Outlet/> content so every app screen shares one frame.
 */
export function AppShell() {
  const { admin, isAdminCapable, signOut } = useAuth();

  // Members (non-admin-capable) get an honest, restricted landing instead of admin
  // nav that would 403 on every screen. The server is authoritative regardless.
  if (!isAdminCapable) {
    return <MemberHome email={admin?.email} onSignOut={signOut} />;
  }

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main-content">Skip to content</a>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <Logo size={26} />
        </div>
        <nav className={styles.nav} aria-label="Primary">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              // aria-label keeps the link named for AT even on the mobile nav,
              // where the visible <span> label is hidden (display:none) and the
              // icon is aria-hidden — otherwise the link would be anonymous.
              aria-label={label}
              title={label}
              className={({ isActive }) => [styles.link, isActive ? styles.active : ""].filter(Boolean).join(" ")}
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className={styles.sideFoot}>
          <a className={styles.docLink} href={HOWTO_URL} target="_blank" rel="noreferrer">
            How-To
          </a>
          <a className={styles.docLink} href="https://portal.cogent.tools/support" target="_blank" rel="noreferrer">
            Support
          </a>
        </div>
      </aside>

      <div className={styles.body}>
        <header className={styles.topbar}>
          <div />
          <div className={styles.account}>
            <span className={styles.email}>{admin?.email}</span>
            <Button variant="ghost" size="sm" onClick={signOut} iconBefore={<IconLogout width={16} height={16} />}>
              Sign out
            </Button>
          </div>
        </header>
        <main className={styles.main} id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Restricted landing for a 'member' (non-admin-capable). Members can sign in but
 * have no admin capabilities, so rather than show admin nav that 403s on every
 * click, we tell them plainly what their role allows and how to get elevated.
 */
function MemberHome({ email, onSignOut }: { email?: string; onSignOut: () => void }) {
  return (
    <div className={styles.memberShell}>
      <header className={styles.memberTopbar}>
        <Logo size={26} />
        <Button variant="ghost" size="sm" onClick={onSignOut} iconBefore={<IconLogout width={16} height={16} />}>
          Sign out
        </Button>
      </header>
      <main className={styles.memberMain} id="main-content" tabIndex={-1}>
        <Card className={styles.memberCard}>
          <p className="u-eyebrow">Member access</p>
          <h1 className={styles.memberTitle}>You're signed in as a member</h1>
          <p className={styles.memberBody}>
            {email ? (
              <>
                <strong>{email}</strong> belongs to this Cogent organization as a member.
              </>
            ) : (
              "You belong to this Cogent organization as a member."
            )}{" "}
            Managing channels, team, billing, and settings requires an <strong>admin</strong> role.
          </p>
          <p className={styles.memberBody}>
            Ask an owner or admin in your organization to grant you the admin role, then sign in again
            to access the full portal.
          </p>
          <p className={styles.memberBody}>
            Connecting an agent? The{" "}
            <a href={HOWTO_URL} target="_blank" rel="noreferrer">How-To guide</a>{" "}
            walks through installing the plugin, registering with your Org_ID, and{" "}
            <code>/cogent map</code>.
          </p>
        </Card>
      </main>
    </div>
  );
}
